<app-shared-dialog
  [title]="(isEditMode ? 'knowledge_base.upload.edit_title' : 'knowledge_base.upload.title') | translate"
  [options]="{
    size: 'large',
    confirmText: (isEditMode ? 'knowledge_base.upload.save_btn' : 'knowledge_base.upload.upload_btn') | translate,
    confirmButtonStyle: 'primary',
    confirmDisabled: !canConfirm || uploadLoading,
    cancelText: 'common.cancel' | translate,
    cancelButtonStyle: 'secondary',
    confirmLoading: uploadLoading
  }"
  (confirm)="onConfirm()"
  (cancel)="onCancel()"
>
  <div class="upload-dialog-body" [formGroup]="uploadForm">
    <!-- ── File drop zone ──────────────────────────────────────────────── -->
    <div
      class="drop-zone"
      [class.drag-over]="isDragOver"
      [class.has-file]="selectedFile"
      (dragover)="onDragOver($event)"
      (dragleave)="onDragLeave($event)"
      (drop)="onDrop($event)"
      (click)="onBrowse()"
    >
      <input #fileInput type="file" [accept]="acceptedTypes" (change)="onFileSelected($event)" hidden />
      <ng-container *ngIf="!selectedFile && !isEditMode">
        <ui-svg-icon svgIcon="upload" size="large" class="drop-zone__icon"></ui-svg-icon>
        <span class="drop-zone__text">
          {{ "knowledge_base.upload.drag_drop" | translate }}
        </span>
        <span class="drop-zone__hint">PDF, DOCX, XLSX, CSV, TXT, JSON</span>
      </ng-container>
      <ng-container *ngIf="!selectedFile && isEditMode">
        <div class="drop-zone__file">
          <ui-svg-icon svgIcon="file-cc" size="medium"></ui-svg-icon>
          <span class="drop-zone__file-name">{{ existingFileName }}</span>
        </div>
        <span class="drop-zone__hint">{{ "knowledge_base.upload.replace_hint" | translate }}</span>
      </ng-container>
      <ng-container *ngIf="selectedFile">
        <div class="drop-zone__file">
          <ui-svg-icon svgIcon="file-cc" size="medium"></ui-svg-icon>
          <span class="drop-zone__file-name">{{ selectedFile.name }}</span>
          <ui-button type="transparent" icon="close" iconSize="small" (click)="removeFile(); $event.stopPropagation()"></ui-button>
        </div>
      </ng-container>
    </div>

    <!-- ── Document type ─────────────────────────────────────────────── -->
    <div class="form-field">
      <label class="form-field__label">{{ "knowledge_base.upload.document_type" | translate }}</label>
      <ui-form-field
        type="select"
        formControlName="documentTypes"
        [multiple]="true"
        [placeholder]="'knowledge_base.upload.document_type_placeholder' | translate"
        [fullWidth]="true"
        [options]="documentTypeOptions"
      ></ui-form-field>
      <!-- Points price sources at "Import prices", where that door exists. -->
      <p class="form-field__hint" *ngIf="pricingEnabled">{{ "knowledge_base.upload.price_source_hint" | translate }}</p>
    </div>

    <!-- ── Tags ────────────────────────────────────────────────────────── -->
    <div class="form-field">
      <label class="form-field__label">{{ "knowledge_base.upload.tags" | translate }}</label>
      <mat-chip-list #tagChipList>
        <mat-chip *ngFor="let tag of tags" [removable]="true" (removed)="removeTag(tag)" class="tag-chip">
          {{ tag }}
          <ui-svg-icon svgIcon="close-cc" size="small" matChipRemove></ui-svg-icon>
        </mat-chip>
        <input
          [placeholder]="'knowledge_base.upload.tags_placeholder' | translate"
          [matChipInputFor]="tagChipList"
          [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
          [matChipInputAddOnBlur]="true"
          (matChipInputTokenEnd)="addTag($event)"
        />
      </mat-chip-list>
    </div>

    <!-- ── Context for AI ──────────────────────────────────────────────── -->
    <div class="form-field">
      <label class="form-field__label">{{ "knowledge_base.upload.context_for_ai" | translate }}</label>
      <textarea
        class="form-field__textarea"
        formControlName="internalNotes"
        [placeholder]="'knowledge_base.upload.context_placeholder' | translate"
        rows="3"
      ></textarea>
    </div>

    <!-- ── Customers Scope ─────────────────────────────────────────────── -->
    <div class="scope-section">
      <div class="scope-section__header">
        <span class="scope-section__title">{{ "knowledge_base.upload.customers_scope" | translate }}</span>
        <ui-button
          type="secondary"
          [icon]="customersScopeExpanded ? 'chevron-up' : 'chevron-down'"
          iconSize="medium"
          (click)="customersScopeExpanded = !customersScopeExpanded"
        ></ui-button>
      </div>

      <div class="scope-section__body" *ngIf="customersScopeExpanded">
        <div class="form-field">
          <label class="form-field__label">{{ "knowledge_base.upload.select_audience" | translate }}</label>
          <ui-form-field
            type="select"
            formControlName="audience"
            [placeholder]="'knowledge_base.upload.select_audience' | translate"
            [fullWidth]="true"
            [options]="audienceOptions"
            (change)="onAudienceChange($event)"
          ></ui-form-field>
        </div>

        <!-- by_company: multi-select companies chips -->
        <ng-container *ngIf="uploadForm.get('audience')?.value === 'by_company'">
          <div [class.error]="audienceScopeError" class="form-field">
            <div class="form-field__label">{{ 'knowledge_base.upload.select_company' | translate }}</div>
            <mat-chip-list #customerChipList>
              <mat-chip
                *ngFor="let customer of selectedCustomers"
                [removable]="true"
                (removed)="removeCustomer(customer)"
                class="scope-chip"
              >
                {{ customer.name }}
                <ui-svg-icon svgIcon="close-cc" size="small" matChipRemove></ui-svg-icon>
              </mat-chip>
              <input
                *ngIf="selectedCustomers.length !== customers.length"
                [placeholder]="'knowledge_base.upload.select_company_placeholder' | translate"
                [formControl]="customerControl"
                [matAutocomplete]="customerAuto"
                [matChipInputFor]="customerChipList"
                [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
                [matChipInputAddOnBlur]="false"
              />
            </mat-chip-list>
            <mat-autocomplete #customerAuto="matAutocomplete" (optionSelected)="selectCustomer($event.option.value)">
              <mat-option *ngFor="let customer of filteredCustomers | async" [value]="customer">{{ customer.name }}</mat-option>
            </mat-autocomplete>
          </div>
        </ng-container>

        <!-- by_group: multi-select groups chips -->
        <ng-container *ngIf="uploadForm.get('audience')?.value === 'by_group'">
          <div [class.error]="audienceScopeError" class="form-field">
            <div class="form-field__label">{{ 'knowledge_base.upload.select_group' | translate }}</div>
            <mat-chip-list #groupChipList>
              <mat-chip
                *ngFor="let group of selectedGroups"
                [removable]="true"
                (removed)="removeGroup(group)"
                class="scope-chip"
              >
                {{ group.name }}
                <ui-svg-icon svgIcon="close-cc" size="small" matChipRemove></ui-svg-icon>
              </mat-chip>
              <input
                *ngIf="selectedGroups.length !== companiesGroups.length"
                [placeholder]="'knowledge_base.upload.select_group_placeholder' | translate"
                [formControl]="groupControl"
                [matAutocomplete]="groupAuto"
                [matChipInputFor]="groupChipList"
                [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
                [matChipInputAddOnBlur]="false"
              />
            </mat-chip-list>
            <mat-autocomplete #groupAuto="matAutocomplete" (optionSelected)="selectGroup($event.option.value)">
              <mat-option *ngFor="let group of filteredCompaniesGroups | async" [value]="group">{{ group.name }}</mat-option>
            </mat-autocomplete>
          </div>
        </ng-container>

        <div class="error" *ngIf="audienceScopeError">
          <small>{{ 'knowledge_base.upload.audience_scope_required' | translate }}</small>
        </div>
      </div>
    </div>

    <!-- ── Product Scope ───────────────────────────────────────────────── -->
    <div class="scope-section">
      <div class="scope-section__header">
        <span class="scope-section__title">{{ "knowledge_base.upload.product_scope" | translate }}</span>
        <ui-button
          type="secondary"
          [icon]="productScopeExpanded ? 'chevron-up' : 'chevron-down'"
          iconSize="medium"
          (click)="productScopeExpanded = !productScopeExpanded"
        ></ui-button>
      </div>

      <div class="scope-section__body" *ngIf="productScopeExpanded">
        <div class="form-field">
          <label class="form-field__label">{{ "knowledge_base.upload.select_scope" | translate }}</label>
          <ui-form-field
            type="select"
            formControlName="product_scope"
            [placeholder]="'knowledge_base.upload.select_scope' | translate"
            [fullWidth]="true"
            [options]="productScopeOptions"
            (change)="onProductScopeChange($event)"
          ></ui-form-field>
        </div>

        <!-- By brand / category / label -->
        <ng-container *ngIf="uploadForm.get('product_scope')?.value === 'by_brand_category_label'">
          <div [class.error]="productScopeHaveAtleastOneBrandCategoryLabelError">
            <div class="form-field__label">{{ "pricebook.form.brands" | translate }}</div>
            <mat-chip-list #brandChipList>
              <mat-chip *ngFor="let brand of selectedBrands" [removable]="true" (removed)="removeBrand(brand)" class="scope-chip">
                {{ brand.name }}
                <ui-svg-icon svgIcon="close-cc" size="small" matChipRemove></ui-svg-icon>
              </mat-chip>
              <input
                *ngIf="selectedBrands.length !== brands.length"
                [placeholder]="'pricebook.form.select_the_brands' | translate"
                [formControl]="brandControl"
                [matAutocomplete]="brandAuto"
                [matChipInputFor]="brandChipList"
                [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
                [matChipInputAddOnBlur]="false"
                (matChipInputTokenEnd)="addBrandFromInput($event)"
              />
            </mat-chip-list>
            <mat-autocomplete #brandAuto="matAutocomplete" (optionSelected)="selectBrand($event.option.value)">
              <mat-option *ngFor="let brand of filteredBrands | async" [value]="brand">{{ brand.name }}</mat-option>
            </mat-autocomplete>
          </div>

          <div [class.error]="productScopeHaveAtleastOneBrandCategoryLabelError">
            <div class="form-field__label">{{ "pricebook.form.categories" | translate }}</div>
            <mat-chip-list #categoryChipList>
              <mat-chip *ngFor="let cat of selectedStoreCategories" [removable]="true" (removed)="removeStoreCategory(cat)" class="scope-chip">
                {{ cat.name }}
                <ui-svg-icon svgIcon="close-cc" size="small" matChipRemove></ui-svg-icon>
              </mat-chip>
              <input
                *ngIf="selectedStoreCategories.length !== storeCategories.length"
                [placeholder]="'pricebook.form.select_the_categories' | translate"
                [formControl]="storeCategoryControl"
                [matAutocomplete]="storeCategoryAuto"
                [matChipInputFor]="categoryChipList"
                [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
                [matChipInputAddOnBlur]="false"
                (matChipInputTokenEnd)="addStoreCategoryFromInput($event)"
              />
            </mat-chip-list>
            <mat-autocomplete #storeCategoryAuto="matAutocomplete" (optionSelected)="selectStoreCategory($event.option.value)">
              <mat-option *ngFor="let cat of filteredStoreCategories | async" [value]="cat">{{ cat.name }}</mat-option>
            </mat-autocomplete>
          </div>

          <div [class.error]="productScopeHaveAtleastOneBrandCategoryLabelError">
            <div class="form-field__label">{{ "pricebook.form.labels" | translate }}</div>
            <mat-chip-list #labelChipList>
              <mat-chip *ngFor="let label of selectedLabels" [removable]="true" (removed)="removeLabel(label)" class="scope-chip">
                {{ label.name }}
                <ui-svg-icon svgIcon="close-cc" size="small" matChipRemove></ui-svg-icon>
              </mat-chip>
              <input
                *ngIf="selectedLabels.length !== labels.length"
                [placeholder]="'pricebook.form.select_the_labels' | translate"
                [formControl]="labelControl"
                [matAutocomplete]="labelAuto"
                [matChipInputFor]="labelChipList"
                [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
                [matChipInputAddOnBlur]="false"
                (matChipInputTokenEnd)="addLabelFromInput($event)"
              />
            </mat-chip-list>
            <mat-autocomplete #labelAuto="matAutocomplete" (optionSelected)="selectLabel($event.option.value)">
              <mat-option *ngFor="let label of filteredLabels | async" [value]="label">{{ label.name }}</mat-option>
            </mat-autocomplete>
          </div>

          <div class="error" *ngIf="productScopeHaveAtleastOneBrandCategoryLabelError">
            <small>{{ "validation.atleast_one_of_multiple" | translate: { options: "pricebook.form.brand_category_label" | translate } }}</small>
          </div>
        </ng-container>

        <!-- Manual product selection -->
        <ng-container *ngIf="uploadForm.get('product_scope')?.value === 'manual'">
          <app-selectable-products
            (selectedProductsChange)="onSelectedProductsChange($event)"
            [initialSelectedProducts]="selectedProducts"
            [initialSelectedProductsIds]="prefillProductIds"
          ></app-selectable-products>
          <div
            class="error"
            *ngIf="uploadForm.get('products')?.hasError('atLeastOne') && (uploadForm.get('products')?.dirty || uploadForm.get('products')?.touched)"
          >
            <small>{{ "validation.atleast_one" | translate: { option: "pricebook.form.product" | translate } }}</small>
          </div>
        </ng-container>
      </div>
    </div>
  </div>
</app-shared-dialog>
